/**
 * Small styled primitive set. Feature components compose these instead of
 * writing raw styles, so the look is driven entirely by theme tokens.
 */
import type { CSSProperties, ReactNode, ButtonHTMLAttributes } from "react";

interface BoxProps {
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
  onClick?: () => void;
  title?: string;
}

export function Box({ children, style, className, onClick, title }: BoxProps) {
  return (
    <div className={className} style={style} onClick={onClick} title={title}>
      {children}
    </div>
  );
}

type ButtonVariant = "primary" | "ghost" | "danger" | "icon";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  children?: ReactNode;
}

const buttonBase: CSSProperties = {
  border: "none",
  borderRadius: "var(--radius-md)",
  fontSize: "var(--font-size-md)",
  fontWeight: "var(--font-weight-medium)" as unknown as number,
  padding: "var(--space-sm) var(--space-lg)",
  transition: "background 0.15s ease, opacity 0.15s ease",
};

const buttonVariants: Record<ButtonVariant, CSSProperties> = {
  primary: {
    background: "var(--color-accent)",
    color: "var(--color-accent-contrast)",
  },
  ghost: {
    background: "transparent",
    color: "var(--color-text)",
  },
  danger: {
    background: "var(--color-danger)",
    color: "var(--color-text-inverse)",
  },
  icon: {
    background: "transparent",
    color: "var(--color-text-muted)",
    borderRadius: "var(--radius-pill)",
    width: 40,
    height: 40,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
  },
};

export function Button({ variant = "primary", children, style, disabled, ...rest }: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled}
      style={{
        ...buttonBase,
        ...buttonVariants[variant],
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

interface TextProps {
  children?: ReactNode;
  muted?: boolean;
  size?: "sm" | "md" | "lg" | "xl";
  weight?: "normal" | "medium" | "bold";
  truncate?: boolean;
  style?: CSSProperties;
}

export function Text({ children, muted, size = "md", weight = "normal", truncate, style }: TextProps) {
  return (
    <span
      style={{
        color: muted ? "var(--color-text-muted)" : "var(--color-text)",
        fontSize: `var(--font-size-${size})`,
        fontWeight: `var(--font-weight-${weight})` as unknown as number,
        ...(truncate
          ? { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }
          : {}),
        ...style,
      }}
    >
      {children}
    </span>
  );
}

interface AvatarProps {
  name: string;
  src?: string | null;
  size?: number;
  online?: boolean;
}

/** Deterministic color from a string, so avatars are stable per user. */
function colorFromString(input: string): string {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = input.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 45%, 55%)`;
}

export function Avatar({ name, src, size = 44, online }: AvatarProps) {
  const initials = name
    .replace(/^@/, "")
    .split(/[\s._-]+/)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join("");

  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      {src ? (
        <img
          src={src}
          alt={name}
          width={size}
          height={size}
          style={{ borderRadius: "var(--radius-pill)", objectFit: "cover", display: "block" }}
        />
      ) : (
        <div
          style={{
            width: size,
            height: size,
            borderRadius: "var(--radius-pill)",
            background: colorFromString(name),
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 600,
            fontSize: size * 0.4,
          }}
        >
          {initials || "?"}
        </div>
      )}
      {online != null && (
        <span
          style={{
            position: "absolute",
            bottom: 0,
            right: 0,
            width: size * 0.28,
            height: size * 0.28,
            borderRadius: "var(--radius-pill)",
            background: online ? "var(--color-online)" : "var(--color-text-muted)",
            border: "2px solid var(--color-panel)",
          }}
        />
      )}
    </div>
  );
}

export function Spinner({ size = 28 }: { size?: number }) {
  return (
    <div
      role="status"
      aria-label="Loading"
      style={{
        width: size,
        height: size,
        border: `${Math.max(2, size / 12)}px solid var(--color-border)`,
        borderTopColor: "var(--color-accent)",
        borderRadius: "var(--radius-pill)",
        animation: "cmc-spin 0.8s linear infinite",
      }}
    />
  );
}
